import { useContext } from 'react'

import useToasterHelper from '../helpers/ToasterHelper'

import { HandleModalContext } from '../layout/mainlayout/MainLayout'
import FirestoreService from '../services/firestoreService'
import UserService from '../services/firebase-service/users/userService'
import HorsesService from '../services/firebase-service/horses/HorsesService'
import HorseCompetitionPaperworkService from '../services/firebase-service/horse-competition-paperwork/HorseCompetitionPaperworkService'

import { UserModel } from '../models/users/user.model'

import { IOption } from '../components/inputs/InputNewMemberRow'
import { IUserInterface } from '../models/users/user.interface'
import { IReqFieldType } from '../pages/competitor/clinicAndOther/Tabs/components/ProfileFields'
import { IHorseCompetitorDocument } from '../models/horse-competiton-paperwork/horse-competiton-paperwork-interface'
import { IHorsesWithPaperwork } from '../components/modals/InfoModals/components/tabs/HorsesTab/HorsesTab'

import { CONST } from '../const/const'
import { MESSAGES_CONST } from '../const/messages-const'
import { MODAL_CONSTS } from '../const/modal-const'
import {
  DISCIPLINE_MEMBERSHIP_FIELDS,
  getHorseNumberFieldName,
  getUserFieldName,
} from '../const/discipline-memberships-const'
import { HorseModel } from '../models/horse/horse.model'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

export const useProfile = () => {
  const toastFunction = useToasterHelper()
  const handleModalContext = useContext(HandleModalContext)

  const onSaveEditUser = async ({
    user,
    setIsEdit,
  }: {
    user: IUserInterface
    setIsEdit: (state: boolean) => void
  }) => {
    try {
      await FirestoreService.updateItem(
        COLLECTIONS.USERS.NAME,
        user.id,
        new UserModel(user).toFirestore()
      )
      setIsEdit(false)
      toastFunction.success({ message: 'Updated successfully!' })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunction.error({ message: error?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const onSaveEditHorse = async ({
    index,
    horsesWithPaperwork,
    setIsEdit,
  }: {
    index: number
    horsesWithPaperwork: IHorsesWithPaperwork[]
    setIsEdit: (state: boolean) => void
  }) => {
    try {
      await FirestoreService.updateItem(
        COLLECTIONS.HORSES.NAME,
        horsesWithPaperwork[index].horse.id,
        new HorseModel(horsesWithPaperwork[index].horse).toFirestore()
      )
      setIsEdit(false)
      toastFunction.success({ message: 'Updated successfully!' })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunction.error({ message: error?.message || MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  const onCancelEditUser = async ({
    user,
    setUser,
    setIsEdit,
  }: {
    user: IUserInterface
    setUser?: (state: IUserInterface) => void
    setIsEdit: (state: boolean) => void
  }) => {
    setIsEdit(false)
    const userInDb = await UserService.getUserById(user.id)
    if (setUser) setUser({ ...userInDb })
  }

  const onCancelEditHorse = async ({
    index,
    horsesWithPaperwork,
    setHorsesWithPaperwork,
    setIsEdit,
  }: {
    index: number
    horsesWithPaperwork: IHorsesWithPaperwork[]
    setHorsesWithPaperwork?: (state: IHorsesWithPaperwork[]) => void
    setIsEdit: (state: boolean) => void
  }) => {
    setIsEdit(false)
    const horseId = horsesWithPaperwork?.[index]?.horse?.id
    const horsePaperworkId = horsesWithPaperwork?.[index]?.paperwork?.id

    if (horseId) {
      const horseInDb = await HorsesService.getById(horseId)
      horsesWithPaperwork[index] = {
        paperwork: horsesWithPaperwork[index].paperwork,
        horse: horseInDb,
      }
    }
    if (horsePaperworkId) {
      const paperworkInDb = await HorseCompetitionPaperworkService.getById(horsePaperworkId)
      horsesWithPaperwork[index] = {
        horse: horsesWithPaperwork[index].horse,
        paperwork: paperworkInDb,
      }
    }
    if (setHorsesWithPaperwork) setHorsesWithPaperwork([...horsesWithPaperwork])
  }

  const onChangeUserInput = ({
    fieldName,
    fieldType,
    fieldKey,
    option,
    user,
    setUser,
  }: {
    fieldName: string
    fieldType?: IReqFieldType
    fieldKey?: string
    user: IUserInterface
    setUser?: (state: IUserInterface) => void
    option?: IOption
  }) => {
    if (
      (fieldType === 'tel' ||
        fieldType === 'text' ||
        fieldType === 'date' ||
        fieldType === 'select') &&
      fieldKey
    ) {
      if (setUser) setUser({ ...user, [fieldKey]: option?.value })
    } else {
      const membership = DISCIPLINE_MEMBERSHIP_FIELDS.find(
        (item) => getUserFieldName(item.key) === fieldKey
      )
      handleModalContext?.handleModal?.(true, MODAL_CONSTS.UPDATE_USER_NUMBER, {
        ...handleModalContext?.dataToPassOn,
        numberLogo: membership?.icon,
        fieldName,
        fieldType,
        fieldKey,
        userData: user,
        onSave: ({ updatedUserData }: { updatedUserData: { [_: string]: string } }) => {
          if (setUser) {
            setUser({ ...user, ...updatedUserData })
          }
        },
      })
    }
  }

  const onChangeHorseInput = ({
    fieldName,
    fieldType,
    fieldKey,
    option,
    index,
    horsesWithPaperwork,
    setHorsesWithPaperwork,
  }: {
    fieldName: string
    fieldType?: IReqFieldType
    fieldKey?: string
    index: number
    horsesWithPaperwork: IHorsesWithPaperwork[]
    setHorsesWithPaperwork?: (state: IHorsesWithPaperwork[]) => void
    option?: IOption
  }) => {
    if (
      (fieldType === 'tel' ||
        fieldType === 'text' ||
        fieldType === 'date' ||
        fieldType === 'select') &&
      fieldKey
    ) {
      horsesWithPaperwork[index] = {
        ...horsesWithPaperwork[index],
        horse: {
          ...horsesWithPaperwork[index].horse,
          [fieldKey]: option?.value,
        },
      }
      if (setHorsesWithPaperwork) setHorsesWithPaperwork([...horsesWithPaperwork])
    } else if (
      fieldKey === 'horseMeasurementCard' ||
      fieldKey === 'horseCoggins' ||
      fieldKey === 'horseHealthCertificate' ||
      fieldKey === 'vaccinationCertificate' ||
      fieldKey === 'horseRhinopneumonitis' ||
      fieldKey === 'horseInfluenza'
    ) {
      handleModalContext?.handleModal?.(true, MODAL_CONSTS.UPDATE_TEAM_DETAILS, {
        ...handleModalContext?.dataToPassOn,
        fieldName,
        fieldKey,
        horsePaperwork: horsesWithPaperwork[index].paperwork,
        selectedHorse: horsesWithPaperwork[index].horse,
        onSave: async (paperworkUpdated: IHorseCompetitorDocument) => {
          horsesWithPaperwork[index] = {
            ...horsesWithPaperwork[index],
            paperwork: {
              ...horsesWithPaperwork[index].paperwork,
              ...paperworkUpdated,
            },
          }
          if (setHorsesWithPaperwork) setHorsesWithPaperwork([...horsesWithPaperwork])
        },
      })
    } else {
      const membership = DISCIPLINE_MEMBERSHIP_FIELDS.find(
        (item) => getHorseNumberFieldName(item.key) === fieldKey
      )
      handleModalContext?.handleModal?.(true, MODAL_CONSTS.UPDATE_USER_NUMBER, {
        ...handleModalContext?.dataToPassOn,
        numberLogo: membership?.icon,
        fieldName,
        fieldType,
        fieldKey,
        isHorse: true,
        horseData: horsesWithPaperwork[index].horse,
        onSave: ({ updateHorseData }: { updateHorseData: { [_: string]: string } }) => {
          horsesWithPaperwork[index] = {
            ...horsesWithPaperwork[index],
            horse: {
              ...horsesWithPaperwork[index].horse,
              ...updateHorseData,
            },
          }
          if (setHorsesWithPaperwork) setHorsesWithPaperwork([...horsesWithPaperwork])
        },
      })
    }
  }

  return {
    onSaveEditUser,
    onSaveEditHorse,
    onCancelEditUser,
    onCancelEditHorse,
    onChangeUserInput,
    onChangeHorseInput,
  }
}
